import { Button, Checkbox, Stack, styled, TextField } from "@mui/material";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import formatter from "../../../tools/formatter.js";
import moment from "moment";
import useDealBuilderSimilarDealsStore from "../../../stores/deal-builder-similar-deals.store.ts";
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import useSimilarDealsAPI from "../../hooks/useSimilarDealsAPI.ts";
import useDealBuilderAlertsStore from "../../../stores/deal-builder-alerts.store.ts";
import { useState } from "react";

function reorder<T>(list: T[], startIndex: number, endIndex: number): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
  userSelect: "none",
  margin: "8px 0px",
  opacity: isDragging ? 0.8 : 1,
  ...draggableStyle
});

const ThinAccordionSummary = styled(AccordionSummary)(() => {
  return {
    "& > span": {
      margin: "0 !important"
    }
  }
});

export function SimilarDealsList() {
  const {
    similarDeals, setSimilarDeals,
    selectedSimilarDealIds, setSelectedSimilarDealIds,
    starredSimilarDealCusips, setStarredSimilarDealCusips,
  } = useDealBuilderSimilarDealsStore();
  const { addUserSimilarDeal } = useSimilarDealsAPI();
  const { setSnackMessage } = useDealBuilderAlertsStore();
  const [cusip, setCusip] = useState<string>("");

  function formatISODate(dateStr: string | number) {
    if (!dateStr) return "-";
    return moment.utc(dateStr).format("MM/DD/YYYY");
  }

  function toggleTrade(event: any, issue_id: number) {
    event.stopPropagation()
    const remove = selectedSimilarDealIds.includes(issue_id);
    if (remove) {
      setSelectedSimilarDealIds([...selectedSimilarDealIds.filter(x => x !== issue_id)]);
    } else {
      setSelectedSimilarDealIds([...selectedSimilarDealIds, issue_id]);
    }
  }

  function deleteSimilarDeal(issue_id: number) {
    const deal = similarDeals.find(x => x.issue_id === issue_id);
    if (deal?.starred) {
      setStarredSimilarDealCusips(starredSimilarDealCusips.filter(c => !deal.cusip.includes(c)));
    }
    setSimilarDeals(similarDeals.filter(x => x.issue_id !== issue_id));
  }

  function toggleStar(e: React.MouseEvent, deal: any) {
    e.preventDefault();
    e.stopPropagation();
    const firstCusip = deal.cusip[0];
    const isStarred = starredSimilarDealCusips.includes(firstCusip);
    const newCusips = isStarred
      ? starredSimilarDealCusips.filter(c => c !== firstCusip)
      : [...starredSimilarDealCusips, firstCusip];
    setStarredSimilarDealCusips(newCusips);
    setSimilarDeals(similarDeals.map(x => x.issue_id === deal.issue_id ? { ...x, starred: !isStarred } : x));
  }

  function onDragEnd(result: any) {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    const newItems = reorder(similarDeals, from, to);
    setSimilarDeals(newItems);
  }

  async function onAddDeal() {
    if (cusip.length < 8 || cusip.length > 9) {
      setSnackMessage("Invalid CUSIP.")
      return;
    }

    const formattedCusip = formatter.cusip(cusip);
    const result = await addUserSimilarDeal(formattedCusip);
    if (!result.valid) setSnackMessage(result.error);
  }

  return (
    <Stack direction="column">

      <Stack direction="row">
        <TextField 
          size="small" 
          value={cusip} 
          onChange={((e) => setCusip(e.target.value))}
          placeholder="CUSIP"
          sx={{ 
            background: "white", 
            flex: 1, 
            "& .MuiOutlinedInput-root": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },  
          }}
        />
        <Button 
          variant="contained" 
          disableElevation 
          onClick={onAddDeal}
          sx={{ 
            color: "white", 
            borderRadius: "0 4px 4px 0" 
          }}
          >Add deal</Button>
      </Stack>
      
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided: any, _snapshot: any) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {similarDeals.map((deal, index) => (
                // @ts-ignore
                <Draggable key={deal.issue_id} draggableId={deal.issue_id} index={index}>
                  {(provided: any, snapshot: any) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={getItemStyle(
                        snapshot.isDragging,
                        provided.draggableProps.style
                      )}
                    >
                      <Accordion key={deal.issue_id}>
                        <ThinAccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography component="span" width="100%">
                            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                              <Stack direction="row" alignItems="center">
                                <DragIndicatorIcon />
                                <Typography component="span">
                                  <Checkbox
                                    checked={selectedSimilarDealIds.includes(deal.issue_id)}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => toggleTrade(e, deal.issue_id)}
                                  />
                                </Typography>
                                {deal.issuer_long_name} ({formatISODate(deal.offering_date)})
                              </Stack>

                              <Stack direction="row" alignItems="center">
                                <Typography component="span">
                                  {deal.starred
                                    ? <StarIcon fontSize="small" sx={{ mt: "4px", mr: "4px", color: "gold" }} onClick={(e) => toggleStar(e, deal)} />
                                    : <StarBorderIcon fontSize="small" sx={{ mt: "4px", mr: "4px", color: "gray" }} onClick={(e) => toggleStar(e, deal)} />
                                  }
                                </Typography>
                                {(deal.user_defined || deal.starred_from_db) && (
                                  <Typography component="span">
                                    <DeleteIcon
                                      color="error"
                                      fontSize="small"
                                      sx={{ mt: "4px", mr: "8px" }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        deleteSimilarDeal(deal.issue_id);
                                      }}
                                    />
                                  </Typography>
                                )}
                              </Stack>
                            </Stack>
                          </Typography>
                        </ThinAccordionSummary>
                        <AccordionDetails onClick={(e) => e.stopPropagation()}>
                          <Stack direction="column" gap={2}>
                            <TableContainer>
                              <Table>
                                <TableBody>
                                  <TableRow>
                                    <TableCell align="left" style={{ fontWeight: 600 }}>Issuer</TableCell>
                                    <TableCell align="left">{deal.issuer_long_name}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell align="left" style={{ fontWeight: 600 }}>Issue</TableCell>
                                    <TableCell align="left">{deal.issue_description}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell align="left" style={{ fontWeight: 600 }}>State</TableCell>
                                    <TableCell align="left">{deal.state}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell align="left" style={{ fontWeight: 600 }}>Amount</TableCell>
                                    <TableCell align="left">{deal.total_offering_amount}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell align="left" style={{ fontWeight: 600 }}>Security</TableCell>
                                    <TableCell align="left">{deal.security_code}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell align="left" style={{ fontWeight: 600 }}>Spline Rating</TableCell>
                                    <TableCell align="left">{deal.spline_lt_rating_composite}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell align="left" style={{ fontWeight: 600 }}>Offering Date</TableCell>
                                    <TableCell align="left">{formatISODate(deal.offering_date)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell align="left" style={{ fontWeight: 600 }}>Dated Date</TableCell>
                                    <TableCell align="left">{formatISODate(deal.dated_date)}</TableCell>
                                  </TableRow>
                                  <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }} >
                                    <TableCell align="left" style={{ fontWeight: 600 }}>Call Date</TableCell>
                                    <TableCell align="left">{formatISODate(deal.next_call_date)}</TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </TableContainer>
                            <TableContainer>
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell align="left" sx={{ fontWeight: 600 }}>Maturity Date</TableCell>
                                    <TableCell align="left" sx={{ fontWeight: 600 }}>Maturity Amount</TableCell>
                                    <TableCell align="left" sx={{ fontWeight: 600 }}>Cusip</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>Year</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>Coupon</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>Spline AAA</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>Spread</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>Diff (bps)</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {deal.maturity_date.map((_, index) => {
                                    const maturityDate = deal.maturity_date[index];
                                    const coupon = deal.coupon[index];
                                    const cusip = deal.cusip[index];
                                    const offering_yield = deal.offering_yield[index];
                                    const amount = formatter.money(deal.total_maturity_offering_amt[index]);
                                    const year = Math.round(moment(maturityDate).diff(moment(deal.dated_date), "days") / 365);

                                    let aaaCurveValue: number | undefined = undefined;
                                    let aaaSpreadValue: number | undefined = undefined;
                                    const aaaValue = deal.spline_aaa[index];
                                    if (aaaValue != null) {
                                      aaaCurveValue = aaaValue;
                                      aaaSpreadValue = offering_yield - aaaValue;
                                    }

                                    return (
                                      <TableRow>
                                        <TableCell align="left">{formatISODate(maturityDate)}</TableCell>
                                        <TableCell align="left">{amount}</TableCell>
                                        <TableCell align="left">{cusip}</TableCell>
                                        <TableCell align="center">{year}</TableCell>
                                        <TableCell align="center">{coupon ? coupon.toFixed(2) + "%" : "N/A"}</TableCell>
                                        <TableCell align="center">{offering_yield ? offering_yield.toFixed(2) + "%" : "N/A"}</TableCell>
                                        <TableCell align="center">{aaaCurveValue !== undefined ? aaaCurveValue.toFixed(2) + "%" : "N/A"}</TableCell>
                                        <TableCell align="center">{aaaSpreadValue !== undefined ? Math.round(aaaSpreadValue * 100) : "N/A"}</TableCell>
                                      </TableRow>
                                    )
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Stack>
    
  );

}
